<section id="projects" class="py-24 sm:py-32 bg-surface-50">
  <div class="container mx-auto px-6 lg:px-10">
    <!-- Section header -->
    <div class="flex items-center gap-4 mb-14" data-aos="fade-up" data-aos-delay="200">
      <span class="font-mono text-[10px] text-accent/40 tracking-widest select-none">///</span>
      <span class="text-accent font-mono text-sm">05</span>
      <h2 class="text-3xl sm:text-4xl font-display font-black text-text-strong">
        {{ "projects.title" | translate }}
      </h2>
      <span class="flex-1 h-px bg-subtle"></span>
    </div>

    <div class="relative max-w-6xl mx-auto">
      <!-- Carousel controls -->
      <div class="flex items-center justify-between mb-6" data-aos="fade-up" data-aos-delay="150">
        <div [attr.data-aos]="isMobile ? 'fade-up' : 'fade-right'" data-aos-delay="150">
          <button
            class="w-10 h-10 rounded-lg border border-subtle bg-surface-100/80 text-muted flex items-center justify-center cursor-pointer hover:border-accent hover:text-accent transition-colors duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
            (click)="prev()" [disabled]="!canPrev" [attr.aria-label]="'projects.carousel.prev' | translate">
            <i class="fas fa-arrow-left text-sm"></i>
          </button>
        </div>
        <div class="flex justify-center gap-2" data-aos="fade-up" data-aos-delay="200">
          @for (project of projects; track $index; let i = $index) {
          <button class="w-2 h-2 rounded-full transition-all duration-300 cursor-pointer"
            [ngClass]="i === currentIndex ? 'bg-accent scale-125' : 'bg-subtle hover:bg-accent/40'"
            (click)="setIndex(i)" [attr.aria-label]="('projects.carousel.goto' | translate) + ' ' + (i + 1)">
          </button>
          }
        </div>
        <div [attr.data-aos]="isMobile ? 'fade-up' : 'fade-left'" data-aos-delay="150">
          <button
            class="w-10 h-10 rounded-lg border border-subtle bg-surface-100/80 text-muted flex items-center justify-center cursor-pointer hover:border-accent hover:text-accent transition-colors duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
            (click)="next()" [disabled]="!canNext" [attr.aria-label]="'projects.carousel.next' | translate">
            <i class="fas fa-arrow-right text-sm"></i>
          </button>
        </div>
      </div>

      @if (currentProject) {
      <article
        class="bg-surface-100/60 rounded-xl overflow-hidden border border-subtle grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] hover:border-accent/20 transition-colors duration-300"
        data-aos="fade-up" data-aos-delay="200">
        <div class="relative p-6 sm:p-10 lg:p-12 min-h-110 flex flex-col justify-center">
          <div class="max-w-xl">
            <div class="flex items-center gap-3 mb-4">
              <span class="h-px w-8 bg-accent/60"></span>
              <span class="text-[10px] uppercase tracking-[0.3em] text-muted font-mono">{{ 'projects.carousel.featured'
                | translate }}</span>
            </div>
            <h3 class="text-3xl sm:text-4xl font-display font-black text-text-strong mb-3">
              {{ currentProject.name }}
            </h3>
            <p class="text-sm text-muted-light leading-relaxed mb-6">
              {{ currentProject.description }}
            </p>
            <div class="mb-6 flex flex-wrap gap-1.5">
              @for (tag of currentProject.tags; track $index) {
              <span
                class="inline-flex items-center gap-1 bg-accent/10 border border-accent/20 text-accent text-xs font-mono px-2.5 py-0.5 rounded">
                {{tag}}
              </span>
              }
            </div>
            <div class="flex items-center gap-2">
              @if (currentProject.github) {
              <a [href]="currentProject.github" target="_blank" rel="noopener noreferrer"
                class="w-10 h-10 rounded-lg border border-subtle flex items-center justify-center text-muted hover:border-accent hover:text-accent transition-colors duration-200"
                [attr.aria-label]="'projects.icons.github' | translate"
                [attr.title]="'projects.icons.github' | translate">
                <i class="fab fa-github text-sm"></i>
              </a>
              }
              @if (currentProject.liveDemo) {
              <a [href]="currentProject.liveDemo" target="_blank" rel="noopener noreferrer"
                class="w-10 h-10 rounded-lg border border-subtle flex items-center justify-center text-muted hover:border-accent hover:text-accent transition-colors duration-200"
                [attr.aria-label]="'projects.icons.liveDemo' | translate"
                [attr.title]="'projects.icons.liveDemo' | translate">
                <i class="fas fa-external-link-alt text-xs"></i>
              </a>
              }
            </div>
          </div>
        </div>

        <div class="relative min-h-85 lg:min-h-110 flex items-center justify-center bg-surface-200/40">
          <div
            class="relative z-10 w-[92%] sm:w-[95%] aspect-12/7 rounded-xl border border-subtle bg-surface-200/60 overflow-hidden flex flex-col"
            [class.cursor-pointer]="currentProject.liveDemo"
            (click)="currentProject.liveDemo && openDemo(currentProject.liveDemo)">
            <!-- Browser chrome -->
            <div
              class="flex items-center gap-1.5 px-3 py-2 bg-surface-300/80 border-b border-subtle text-[10px] font-mono text-muted shrink-0">
              <span class="h-2 w-2 rounded-full bg-red-500/60"></span>
              <span class="h-2 w-2 rounded-full bg-yellow-500/60"></span>
              <span class="h-2 w-2 rounded-full bg-green-500/60"></span>
              <span class="ml-2 truncate">{{ currentProject.liveDemo || currentProject.name }}</span>
              @if (currentProject.liveDemo) {
              <i class="fas fa-external-link-alt ml-auto text-muted/50"></i>
              }
            </div>
            <div class="relative flex-1 min-h-0">
              <img [src]="currentProject.image || 'images/projects/placeholder.webp'" [alt]="currentProject.name"
                class="w-full h-full object-cover object-center brightness-[0.92]" loading="lazy" width="1200"
                height="700" />
            </div>
          </div>
          <div class="absolute inset-0 bg-linear-to-l from-surface-100/60 via-transparent to-transparent"></div>
        </div>
      </article>
      }
    </div>
  </div>
</section>